import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserSession } from '../contexts/UserContext.js';
import { isDevelopment } from '../utils/environment.js';
import './Landing.css';
import './RequestAccessModal.css';
import '../App.css';

import earth from '../Ui/treedee3.webp';
import plant from '../Ui/abs2.webp';
import blob from '../Ui/aibrain3.webp'
import computer from '../Ui/computer2.webp'
import loader from '../Ui/loading2.gif'
import icon1 from '../Ui/wired-gradient-500-fingerprint-security.gif'
import icon2 from '../Ui/wired-gradient-1121-iota-internet-of-things.gif'
import icon3 from '../Ui/wired-gradient-1827-growing-plant.gif'
import iconEditDoc from '../Ui/wired-gradient-1534-paper-boat-hover-pinch-ezgif.com-webp-to-gif-converter.gif'
import computer3 from '../Ui/computer4.webp'
import aibrain from '../Ui/aibrain4.webp';
import floating from '../Ui/floatinglady-better2.webp'
import RequestAccessModal from './RequestAccessModal.js';
import FomoOverlay from './FomoOverlay.js';

import TopMenu from './topMenu.js';

const Landing = ({ forceModalOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, loginWithRedirect } = useAuth0();
    const { userSession, loadUserSession, hasElevatedAccess, isGodUser, isLoadingSession } = useUserSession();
    const [isModalVisible, setIsModalVisible] = useState(!!forceModalOpen);
    const [fomoOverlay, setFomoOverlay] = useState({ isVisible: false, featureName: '' });

    // Check if we're on localhost for dev mode - SECURE IMPLEMENTATION
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const allowDevBypass = useMemo(() => isLocalhost && isDevelopment(), [isLocalhost]);

    const POST_AUTH_STEP_KEY = 'postAuthStep';

    const handleNavigate = useCallback((path) => {
        navigate(path);
    }, [navigate]);

    const showModal = useCallback(async () => {
        // On localhost in dev mode, skip auth and just open modal
        if (allowDevBypass) {
            if (location.pathname !== '/onboarding') {
                navigate('/onboarding');
            }
            setIsModalVisible(true);
            return;
        }
        
        // AUTHENTICATE IMMEDIATELY when any onboarding button is clicked
        if (!isAuthenticated) {
            localStorage.setItem(POST_AUTH_STEP_KEY, 'openModal');
            await loginWithRedirect();
            return;
        }
        
        // User is authenticated - load session, determine correct step, and open modal
        try {
            // Load session to ensure we have latest data for step calculation
            await loadUserSession();
            
            // The modal itself will use getModalStep() to determine the correct step
            // So we just need to open it and let it handle the step logic internally
            
            // Navigate to onboarding route if needed
            if (location.pathname !== '/onboarding') {
                navigate('/onboarding');
            }
            
            // Open modal - it will internally determine the correct step
            setIsModalVisible(true);
            
        } catch (error) {
            // Still open modal even if session loading fails - modal can handle it
            setIsModalVisible(true);
        }
    }, [allowDevBypass, location.pathname, navigate, isAuthenticated, loginWithRedirect, loadUserSession]);

    const closeModal = useCallback(() => {
        setIsModalVisible(false);
        
        // Clear any localStorage that might cause re-opening
        localStorage.removeItem(POST_AUTH_STEP_KEY);
    }, []);

    const meow = '>>>>>>>>>>>>>>>>';
    const meow2 = '<<<<<<<<<<<<<<<';
    
    // Auto-open modal if forced by route (only once, not after user closes)
    useEffect(() => {
        if (forceModalOpen && !isModalVisible) {
            setIsModalVisible(true);
        }
    }, [forceModalOpen]); // Removed isModalVisible dependency to prevent re-opening after close

    // Handle post-auth redirect to modal
    useEffect(() => {
        const handlePostAuth = async () => {
            if (isAuthenticated) {
                const savedStep = localStorage.getItem(POST_AUTH_STEP_KEY);
                if (savedStep) {
                    localStorage.removeItem(POST_AUTH_STEP_KEY);
                    
                    try {
                        // Load session to ensure modal opens at correct step
                        await loadUserSession();
                        
                        // Navigate to onboarding route for modal
                        if (location.pathname !== '/onboarding') {
                            navigate('/onboarding');
                        }
                        
                        // Open modal - it will determine the correct step internally
                        setIsModalVisible(true);
                    } catch (error) {
                        // Still open modal even if session loading fails
                        setIsModalVisible(true);
                    }
                }
            }
        };
        
        handlePostAuth();
    }, [isAuthenticated, navigate, location.pathname, loadUserSession]);

    return (
            <>
            <div className='landing'>
            <TopMenu isLandingPage={true} handleViewChange={(page) => {
                if (page === 'dashboard' || page === '') {
                    // Home button - only for authorized users (Immortal/God)
                    if ((allowDevBypass && userSession.isLoaded && hasElevatedAccess()) || 
                        (isAuthenticated && userSession.isLoaded && hasElevatedAccess())) {
                        navigate('/home');
                    }
                } else if (page === 'god') {
                    // Only God users can access God panel
                    if (isGodUser()) {
                        navigate('/home', { state: { initialView: 'god' } });
                    }
                } else if (['profile', 'settings', 'feedback'].includes(page)) {
                    // Check if user is authenticated AND has paid
                    if (isAuthenticated && userSession.isLoaded && userSession.hasPaid) {
                        // Paid users go to home with specific view (like God panel)
                        navigate('/home', { state: { initialView: page } });
                    } else {
                        // Unpaid or unauthenticated users see FOMO overlay
                        const featureNames = {
                            'profile': 'Profile Management',
                            'settings': 'Account Settings', 
                            'feedback': 'Feedback System'
                        };
                        setFomoOverlay({ 
                            isVisible: true, 
                            featureName: featureNames[page] || page 
                        });
                    }
                } else {
                    navigate('/' + page);
                }
            }} />

<div className='containerTop'>
    <div className='Left'>
        <div className="hero">
        <h1 className='IntellegacyText'>Intellegacy</h1>
            <h2>Upload yourself to the cloud.</h2>
            <p>We started this exciting project with the vision of preserving human minds.

                <br/>
Granted, you won't be immortal tomorrow, but you <b>CAN</b> indeed be amongst the first to start 
uploading your consciousness to the cloud, and pave the way to real immortality.
</p><h3>Be amongst the first to do so, with Intellegacy.</h3>

<button onClick={showModal} className="act-button">
    Request First Access
</button> &nbsp;

{/* Show "Enter System" button for God/Immortal users */}
{((allowDevBypass && userSession.isLoaded && hasElevatedAccess()) || (isAuthenticated && userSession.isLoaded && hasElevatedAccess())) && (
    <button onClick={() => navigate('/home')} className="act-button" style={{backgroundColor: '#gold', color: '#000'}}>
        Enter System
    </button>
)} &nbsp;

{/* <button onClick={() => handleNavigate('/')} className="act-button">Request First Access</button> &nbsp; */}

<img className='leftelement' src={ aibrain } alt='Element'></img>

        
        </div>
    </div>
    <div className='Right'>
    <img className='element' src={ computer } alt='Element'></img>
    </div>

    
</div>

<div className='sep'>
    { meow }
</div>
<br/>

<div className='Page2'>

<br/>
<h1 className='firstAccess'>
    PRIORITY ACCESS <img className='loaderIc' src={ loader } alt=''/>
</h1>
            <div className="info">
            <p>We are looking to grant first access &nbsp;<p className='ExtraT'>TO UP TO A 1000 USERS</p>
            &nbsp; who will help us by trying out our system and giving us
            feedback, allowing us to improve and take the idea and the tech forward.
            This replica of you will serve as a guide 
            to a library of your life that you will be 
            able to build and customize, that will help you organize everything in one convenient place
            to be reached easily when ever you need, as well as be left 
            to live long after you, just the way you design it.</p>

            </div>
    {/* <img className='earth' src={ floating } alt='partnership'></img> */}
    <img className='earth' src={ plant } alt='partnership'></img>
            <br/>


            <button onClick={showModal} className="act-button">
                Sign Up Now
            </button>
            <br/><br/>
</div>

<br/>

<div className="hero">

<div className='CoverInterceptor'>

<h1 className='innerCoverText'>We are here to revolutionize life and death,
and with time and your participation we are going to work towards improving these technologies and implementing even more uses for them.</h1>

</div>

<br/><br/>

<div className='ContainerSmolWrapper'>
<div className='ContainerSmol'>
    <h3 className='topTextin'>Train your Ai</h3>
    <img className='AnimIco' src={ iconEditDoc } alt=''/>
    <p className='bottomTextin'>A bundle of Ai models that'll learn from you & be trained on your data to help organize your life library and eternalize your mind.</p>
</div>
<div className='ContainerSmol'>
    <h3 className='topTextin'>Leave a Legacy</h3>
    <img className='AnimIco' src={ icon1 } alt=''/>
    <p className='bottomTextin'>Build a hyper-realistic Ai digital Humanoid of yourself that'll serve as a guide through all your files, media, and memories.</p>
</div>
<div className='ContainerSmol'>
    <h3 className='topTextin'>Revolutionize</h3>
    <img className='AnimIco' src={ icon3 } alt=''/>
    <p className='bottomTextin'>Intellegacy will challenge mortality, make history personal, alter the face of Ai, and also work toward making the earth greener.</p>
</div>
</div>
<br/>

<button onClick={showModal} className="act-button">
    Build Yourself
</button>
            </div>


<div className="info2">
<div className='Left2'>

<img className='compLeft' src={ computer3 } alt=''/>

</div>
<div className='Right2'>

<p className='inBetweenText'>
<h1 className='WhatIsIntellegacy'>
What is Intellegacy?
</h1><h2>
A FUTURE WHERE NO ONE DIES
</h2>
An agent based app, syncing all your media in one place with an Ai clone of you
to manage it with.<br/>
The clone is interactive and proactive 
(It can message you first, like you would - a friend).
It will have your face, your voice and learn from every interaction.
It can serve as a prosthetic memory, an interactive journal and a means of self help and organization.
<br/>
We are privacy focused, none of the profiles are initially public.
In time we will integrate our product with green burrial, allowing Intellegacy life libraries to be embedded in trees, creating green forest-like burial sites. <br/><br/>
Instead of leaving behind a tombstone with a name and date, <br/> we will get to leave 
behind growth and rebirth, true legacy and individual history, our intelligence, 
and our actual lives</p> <p className='ExtraT'>the way WE choose to.</p>
</div>
</div>

<img className='leftelement2' src={ blob } alt='Element'/>
<button onClick={showModal} className="act-button">
    Ask to Join us
</button>
        
<br/><br/>
<div className='sep'>
    { meow2 } &nbsp;&nbsp;
</div>
<div className='CoverInterceptor2'>

<div className='info'>
<h1 className='userBenefit'>
<img className='loaderIc' src={ loader } alt=''/>    USER BENEFIT
</h1>
<p>
Start training our Ai models once we grant access, 
and you may end up sending your clone to an e-meeting, becoming part of a mass collective consciousness that 
solves issues the human race is facing,
or at the very least, be known much more personally by your great grandchildren and the generations to come, 
take part in writing human history, be environmentally consious 
and <br/><br/><p className='ExtraT'>BECOME THE FUTURE.</p></p>

<center>
<img className='earth' src={ earth } alt='rebirth'/>
<br/>
<button onClick={showModal} className="act-button">
    Start Here
</button>
        
</center>


            <br/>

</div>

</div>

<br/><br/>
<div className='info3'>

All rights reserved, Intellegacy &#169; 2023-2025

</div>
<br/>


                </div>

                {/* Loading Overlay */}
                {isLoadingSession && (
                    <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                    </div>
                )}

                {isModalVisible && <RequestAccessModal onClose={closeModal} />}
                
                {fomoOverlay.isVisible && (
                    <FomoOverlay 
                        isVisible={fomoOverlay.isVisible}
                        featureName={fomoOverlay.featureName}
                        onClose={() => setFomoOverlay({ isVisible: false, featureName: '' })}
                    />
                )}

                </>
    );
};

export default Landing;